import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import netlifyIdentity from 'netlify-identity-widget';

const IdentityContext = createContext(null);

// True on a local hostname under `npm start`/`netlify dev` (react-scripts
// inlines NODE_ENV to 'development' there and 'production' for
// `npm run build`, which is what Netlify's build actually deploys). Used
// only to fix a netlify-identity-widget quirk below — not to bypass any
// credential check.
const IS_LOCAL_HOST =
  process.env.NODE_ENV === 'development' &&
  ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);

export function IdentityProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    let settled = false;
    const onInit = (u) => {
      settled = true;
      setUser(u || null);
      setInitializing(false);
    };
    const onLogin = (u) => setUser(u);
    const onLogout = () => setUser(null);

    netlifyIdentity.on('init', onInit);
    netlifyIdentity.on('login', onLogin);
    netlifyIdentity.on('logout', onLogout);
    // On a local hostname, the widget's own internal detection refuses to
    // construct a real auth client at all unless a site URL is already
    // cached from a previous session — it just silently no-ops instead of
    // ever attempting real login. Passing an explicit APIUrl bypasses that
    // heuristic and points it at this origin's own identity path, which
    // `netlify dev` proxies to the real backend, so real login actually
    // works locally too. Harmless on the deployed site: that's exactly the
    // URL the widget already computes by default when not on a local host.
    netlifyIdentity.init(IS_LOCAL_HOST ? { APIUrl: `${window.location.origin}/.netlify/identity` } : undefined);

    // The widget's 'init' event depends on a settings fetch to
    // /.netlify/identity/settings resolving as JSON. If Identity isn't
    // reachable — running via plain `npm start` instead of `netlify dev`,
    // or before Identity is enabled on the live site — that fetch gets the
    // SPA's HTML fallback instead, the widget's JSON parse throws, and
    // 'init' never fires, leaving the admin gate spinning forever. Fall
    // back to "logged out" so the app degrades to the login screen instead.
    const timeout = setTimeout(() => {
      if (!settled) {
        setUser(null);
        setInitializing(false);
      }
    }, 4000);

    return () => {
      clearTimeout(timeout);
      netlifyIdentity.off('init', onInit);
      netlifyIdentity.off('login', onLogin);
      netlifyIdentity.off('logout', onLogout);
    };
  }, []);

  const authFetch = useCallback(async (url, options = {}) => {
    const current = netlifyIdentity.currentUser();
    const jwt = current ? await current.jwt() : null;
    const headers = { ...(options.headers || {}) };
    if (jwt) headers.Authorization = `Bearer ${jwt}`;
    return fetch(url, { ...options, headers });
  }, []);

  // Bypasses the widget's own modal (which doesn't offer a show-password
  // toggle and doesn't auto-dismiss on success) and drives its underlying
  // store directly, so our own form fully controls the UI. `store` is part
  // of netlify-identity-widget's public API surface, used internally by the
  // built-in modal for the exact same call.
  const loginWithPassword = useCallback(async (email, password) => {
    await netlifyIdentity.store.login(email, password, true);
    const current = netlifyIdentity.currentUser();
    if (!current) {
      const storeError = netlifyIdentity.store.error;
      throw new Error((storeError && storeError.message) || 'Invalid email or password.');
    }
    return current;
  }, []);

  const logout = useCallback(() => netlifyIdentity.logout(), []);

  return (
    <IdentityContext.Provider value={{ user, initializing, authFetch, loginWithPassword, logout }}>
      {children}
    </IdentityContext.Provider>
  );
}

export function useIdentity() {
  const ctx = useContext(IdentityContext);
  if (!ctx) throw new Error('useIdentity must be used within IdentityProvider');
  return ctx;
}
